import { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Send, User, Truck, Building2, Plus, ArrowLeft, Loader2, Trash2, Megaphone, Users, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export default function Messages({ user }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [loads, setLoads] = useState([]);
  const [selectedLoadId, setSelectedLoadId] = useState('');
  // Team Broadcast state
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [teamAgents, setTeamAgents] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState(new Set());
  const [sendToAll, setSendToAll] = useState(true);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastPriority, setBroadcastPriority] = useState('normal');
  const [broadcasting, setBroadcasting] = useState(false);
  const messagesEndRef = useRef(null);

  const canBroadcast = user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'broker_manager';

  useEffect(() => {
    loadConversations();
    loadLoads();
    
    // Mark driver message notifications as read when visiting Messages page
    api.get('/notifications').then(res => {
      const driverNotifs = (res.data || []).filter(n => n.type === 'driver_message' && !n.is_read);
      driverNotifs.forEach(n => api.put(`/notifications/${n.id}/read`).catch(() => {}));
    }).catch(() => {});
    
    // Poll for new messages every 10 seconds
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = () => {
    api.get('/messages/conversations')
      .then(res => setConversations(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  const loadLoads = () => {
    api.get('/loads')
      .then(res => setLoads(res.data))
      .catch(err => console.error(err));
  };

  const loadMessages = (conversationId) => {
    api.get(`/messages/conversations/${conversationId}`)
      .then(res => {
        setMessages(res.data.messages);
        // Update conversation in list to clear unread
        setConversations(prev => prev.map(c => 
          c.id === conversationId ? { ...c, unread_count: 0 } : c
        ));
      })
      .catch(err => toast.error('Failed to load messages'));
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      const res = await api.post(`/messages/conversations/${selectedConversation.id}/send`, {
        content: newMessage
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      loadConversations();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async () => {
    if (!selectedLoadId) {
      toast.error('Please select a load');
      return;
    }

    try {
      const res = await api.post(`/messages/load/${selectedLoadId}/start`);
      setShowNewDialog(false);
      setSelectedLoadId('');
      loadConversations();
      setSelectedConversation(res.data);
      toast.success('Conversation started!');
    } catch (err) {
      toast.error('Failed to start conversation');
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation and all messages? This cannot be undone.')) return;
    
    try {
      await api.delete(`/messages/conversations/${convId}`);
      toast.success('Conversation deleted');
      if (selectedConversation?.id === convId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete conversation');
    }
  };

  const openBroadcastDialog = () => {
    setBroadcastSubject('');
    setBroadcastBody('');
    setBroadcastPriority('normal');
    setSelectedRecipients(new Set());
    setSendToAll(true);
    setShowBroadcast(true);
    api.get('/messages/team-agents')
      .then(res => setTeamAgents(res.data || []))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load team'));
  };

  const toggleRecipient = (id) => {
    setSelectedRecipients(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const sendBroadcast = async () => {
    if (!broadcastBody.trim()) { toast.error('Message body is required'); return; }
    if (!sendToAll && selectedRecipients.size === 0) { toast.error('Pick at least one recipient or send to All'); return; }
    setBroadcasting(true);
    try {
      const res = await api.post('/messages/broadcast', {
        recipients: sendToAll ? 'all' : Array.from(selectedRecipients),
        subject: broadcastSubject,
        body: broadcastBody,
        priority: broadcastPriority,
      });
      toast.success(`Message delivered to ${res.data.recipients} team member${res.data.recipients === 1 ? '' : 's'}`);
      setShowBroadcast(false);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally { setBroadcasting(false); }
  };

  const getParticipantIcon = (type) => {
    switch (type) {
      case 'carrier': return <Building2 className="w-4 h-4" />;
      case 'driver': return <Truck className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Messages</h1>
          <p className="text-slate-600 mt-1">Communicate with drivers and carriers</p>
        </div>
        <div className="flex gap-2">
          {canBroadcast && (
            <Button
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={openBroadcastDialog}
              data-testid="broadcast-team-btn"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Broadcast to Team
            </Button>
          )}
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-conversation-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Conversation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start New Conversation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select a Load</Label>
                <Select value={selectedLoadId} onValueChange={setSelectedLoadId}>
                  <SelectTrigger data-testid="load-select">
                    <SelectValue placeholder="Choose a load..." />
                  </SelectTrigger>
                  <SelectContent>
                    {loads.map(load => (
                      <SelectItem key={load.id} value={load.id}>
                        {load.load_number} - {load.pickup_city} → {load.delivery_city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  This will create a group chat with all parties on the load (dispatcher, carrier, driver)
                </p>
              </div>
              <Button onClick={startNewConversation} className="w-full" data-testid="start-conversation-btn">
                Start Conversation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Team Broadcast Dialog */}
      {canBroadcast && (
        <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-purple-600" /> Broadcast Message to Your Team
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Recipients</Label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={sendToAll} onChange={e => setSendToAll(e.target.checked)} data-testid="send-to-all-check" />
                    <span>Send to <strong>ALL</strong> ({teamAgents.length})</span>
                  </label>
                </div>
                {!sendToAll && (
                  <div className="max-h-56 overflow-y-auto border rounded-lg p-2 space-y-1 bg-slate-50" data-testid="recipient-list">
                    {teamAgents.map(a => (
                      <label key={a.id} className="flex items-center gap-2 p-1.5 hover:bg-white rounded cursor-pointer">
                        <input type="checkbox" checked={selectedRecipients.has(a.id)} onChange={() => toggleRecipient(a.id)} />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{a.name}</span>
                          <span className="text-xs text-slate-500 ml-2">{(a.role || 'agent').replace('_',' ')}</span>
                          {a.email && <span className="text-xs text-slate-400 ml-2">· {a.email}</span>}
                          {!a.email && <span className="text-xs text-amber-600 ml-2">· no email</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Channels */}
              <div>
                <Label className="text-sm font-semibold">Delivery Channels</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" id="ch-inapp" data-testid="channel-inapp"
                      defaultChecked={true} />
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">In-App Message</span>
                  </label>
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" id="ch-email" data-testid="channel-email" />
                    <Send className="w-4 h-4 text-amber-600" />
                    <span className="text-sm">Email</span>
                  </label>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Emails send from your franchise Gmail (or master admin if not configured). Users without an email address will be skipped for email.</p>
              </div>

              {/* Priority */}
              <div>
                <Label className="text-sm font-semibold">Priority</Label>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setBroadcastPriority('normal')}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${broadcastPriority === 'normal' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-700 border-slate-300'}`}
                    data-testid="priority-normal">Normal</button>
                  <button
                    type="button"
                    onClick={() => setBroadcastPriority('urgent')}
                    className={`px-3 py-1.5 rounded-lg text-sm border flex items-center gap-1 ${broadcastPriority === 'urgent' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-700 border-rose-300'}`}
                    data-testid="priority-urgent">
                    <AlertTriangle className="w-3 h-3" /> Urgent
                  </button>
                </div>
              </div>

              {/* Templates — one-click prefills for weekly comms */}
              <div>
                <Label className="text-sm font-semibold">Quick Templates</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                  {[
                    {
                      key: 'weekly-rate',
                      label: 'Weekly Rate Update',
                      color: 'border-blue-300 hover:bg-blue-50 text-blue-700',
                      subject: `Weekly Rate Update — ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`,
                      body: `Team,\n\nOur fleet rate minimums for this week are:\n\n• Cargo Van: $1.80/mi\n• Sprinter Van: $2.00/mi\n• Box Truck: $2.50/mi\n• Semi Truck: $2.20/mi\n\nBook only at or above these floors. If a broker won't move, escalate to me and I'll negotiate.\n\nThanks,\n${user?.name || 'Manager'}`,
                    },
                    {
                      key: 'loadboard-alert',
                      label: 'Load Board Alert',
                      color: 'border-emerald-300 hover:bg-emerald-50 text-emerald-700',
                      subject: `Load Board Alert — Hot Lanes Today`,
                      body: `Team,\n\nHeads up — heavy demand on the following lanes today:\n\n• GA → FL (Reefer paying $3+/mi)\n• TX → CA (Dry van backhaul opportunity)\n• IL → OH (Flatbed spike)\n\nHit these first while rates are elevated. Report booked loads in the group.\n\n${user?.name || 'Manager'}`,
                    },
                    {
                      key: 'compliance',
                      label: 'Compliance Reminder',
                      color: 'border-amber-300 hover:bg-amber-50 text-amber-700',
                      subject: `Compliance Reminder — POD & BOL`,
                      body: `Team,\n\nQuick reminder — every completed load MUST have BOL + POD uploaded before end of day.\n\n1. Snap the paperwork with your driver camera\n2. Upload it to your dispatcher folder\n3. Do NOT wait until Friday to catch up\n\nMissed uploads delay factoring payment for the entire team.\n\nThanks,\n${user?.name || 'Manager'}`,
                    },
                  ].map(t => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setBroadcastSubject(t.subject);
                        setBroadcastBody(t.body);
                        toast.success(`Loaded ${t.label} template`);
                      }}
                      className={`text-xs font-medium border rounded-lg px-3 py-2 bg-white transition-colors text-left ${t.color}`}
                      data-testid={`template-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label className="text-sm font-semibold">Subject (optional)</Label>
                <Input
                  value={broadcastSubject}
                  onChange={e => setBroadcastSubject(e.target.value)}
                  placeholder="e.g. Morning team update"
                  maxLength={120}
                  data-testid="broadcast-subject"
                />
              </div>

              {/* Body */}
              <div>
                <Label className="text-sm font-semibold">Message</Label>
                <textarea
                  className="w-full min-h-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={broadcastBody}
                  onChange={e => setBroadcastBody(e.target.value)}
                  placeholder="Type your message here — it will be sent to every selected recipient..."
                  data-testid="broadcast-body"
                />
                <p className="text-[11px] text-slate-500 mt-1">Recipients: {sendToAll ? `ALL ${teamAgents.length} team members` : `${selectedRecipients.size} selected`}</p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowBroadcast(false)}>Cancel</Button>
                <Button
                  onClick={() => {
                    // Read channel checkboxes at send time
                    const chs = [];
                    if (document.getElementById('ch-inapp')?.checked) chs.push('inapp');
                    if (document.getElementById('ch-email')?.checked) chs.push('email');
                    if (chs.length === 0) { toast.error('Pick at least one channel'); return; }
                    // patch sendBroadcast to use these channels
                    setBroadcasting(true);
                    api.post('/messages/broadcast', {
                      recipients: sendToAll ? 'all' : Array.from(selectedRecipients),
                      subject: broadcastSubject,
                      body: broadcastBody,
                      priority: broadcastPriority,
                      channels: chs,
                    }).then(res => {
                      const parts = [];
                      if (res.data.inapp_delivered) parts.push(`${res.data.inapp_delivered} in-app`);
                      if (res.data.email_sent) parts.push(`${res.data.email_sent} email`);
                      toast.success(`Broadcast sent: ${parts.join(' + ') || 'delivered'}`);
                      if (res.data.email_failed?.length) toast.warning(`${res.data.email_failed.length} email(s) failed — check your SMTP config`);
                      setShowBroadcast(false);
                      loadConversations();
                    }).catch(err => toast.error(err.response?.data?.detail || 'Broadcast failed'))
                      .finally(() => setBroadcasting(false));
                  }}
                  disabled={broadcasting || !broadcastBody.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="send-broadcast-btn"
                >
                  {broadcasting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Broadcast</>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
        {/* Conversations List */}
        <Card className="lg:col-span-1 overflow-hidden flex flex-col">
          <CardHeader className="border-b bg-slate-50">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Conversations
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {totalUnread}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No conversations yet</p>
                <p className="text-sm">Start a new conversation from a load</p>
              </div>
            ) : (
              <div className="divide-y">
                {conversations.map(conv => (
                  <div
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          Load #{conv.load_number || 'N/A'}
                        </p>
                        <div className="flex items-center gap-1 mt-1">
                          {conv.participants?.slice(0, 3).map((p, i) => (
                            <span key={i} className="text-xs text-slate-500 flex items-center gap-1">
                              {getParticipantIcon(p.type)}
                              {p.name?.split(' ')[0]}
                              {i < Math.min(conv.participants.length - 1, 2) && ','}
                            </span>
                          ))}
                        </div>
                        {conv.last_message && (
                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {conv.last_message}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {conv.unread_count > 0 && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                        {user?.role === 'super_admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => deleteConversation(conv.id, e)}
                            data-testid={`delete-conv-${conv.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-2 overflow-hidden flex flex-col">
          {selectedConversation ? (
            <>
              <CardHeader className="border-b bg-slate-50">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="lg:hidden"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <CardTitle className="text-lg">Load #{selectedConversation.load_number}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedConversation.participants?.map((p, i) => (
                        <span 
                          key={i} 
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            p.type === 'dispatcher' ? 'bg-blue-100 text-blue-700' :
                            p.type === 'carrier' ? 'bg-green-100 text-green-700' :
                            'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {p.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          msg.sender_id === user.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        {msg.sender_id !== user.id && (
                          <p className="text-xs font-medium mb-1 opacity-75">
                            {msg.sender_name} ({msg.sender_type})
                          </p>
                        )}
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.sender_id === user.id ? 'text-blue-200' : 'text-slate-400'
                        }`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </CardContent>

              <div className="p-4 border-t bg-white">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button onClick={handleSend} disabled={sending || !newMessage.trim()} data-testid="send-btn">
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm">Or start a new one from a load</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
